import { useCallback, useState } from 'react';

export const useEditableTableLogic = <T extends Record<string, any>>({
	formMethods,
}: {
	formMethods: any;
}) => {
	const [newRows, setNewRows] = useState<Record<string, Partial<T>>>({});
	const [deletedRowKeys, setDeletedRowKeys] = useState<Set<string>>(new Set());

	const [isEditing, setIsEditing] = useState(false);
	const [isAdding, setIsAdding] = useState(false);

	const { reset } = formMethods;

	const addRow = useCallback(() => {
		const tempId = `new-${Date.now()}`;
		setNewRows((prev) => ({
			...prev,
			[tempId]: {},
		}));
		setIsAdding(true);
	}, []);

	const toggleRowDeleted = useCallback((key: string | number) => {
		if (typeof key !== 'string') {
			key = String(key);
		}
		setDeletedRowKeys((prev) => {
			const next = new Set(prev);
			next.has(key) ? next.delete(key) : next.add(key);
			return next;
		});
	}, []);

	const clearNewRows = useCallback(() => {
		setNewRows({});
		setIsAdding(false);
		reset();
	}, []);

	const onCancel = () => {
		setIsEditing(false);
		setIsAdding(false);
		setDeletedRowKeys(new Set());
		clearNewRows();
		reset();
	};

	const clearNewRow = useCallback(
		(key: string) => {
			setNewRows((prev) => {
				const { [key]: _, ...rest } = prev;

				formMethods.unregister(key);

				if (Object.keys(rest).length === 0) {
					setIsAdding(false);
				}

				return rest;
			});
		},
		[setIsAdding, formMethods],
	);

	const isRowDeleted = useCallback(
		(key: string | number) => deletedRowKeys.has(typeof key === 'number' ? String(key) : key),
		[deletedRowKeys],
	);

	return {
		newRows,
		addRow,
		clearNewRows,
		isEditing,
		setIsEditing,
		isAdding,
		setIsAdding,
		onCancel,
		clearNewRow,
		toggleRowDeleted,
		isRowDeleted,
		deletedRowKeys,
	};
};
